import useCheckPC from "@repo/hooks/utils/useCheckPC";
import useScrollLock from "@repo/hooks/utils/useScrollLock";
import { transformComma } from "@repo/utils/src/number";
import type { Filter } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import qs from "query-string";
import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import Button from "@components/atoms/Button";
import Icon from "@components/atoms/Icon";
import Checkbox from "@components/atoms/Input/Checkbox";
import Logo from "@components/atoms/Logo";
import FilterItem from "@components/molecules/Filter/FilterItem";
import FilterList from "@components/molecules/Filter/FilterList";

import { ACCESSORIES, CATEGORY, CLOTHING, FOOTWEAR } from "@const/constants";
import { IconName } from "@const/enum";

interface IFilterProps {
  closeFilter: () => void;
  activeFilterItem: string;
  setActiveFilterItem: (value: string) => void;
  filters: Filter[];
  totalProductCount: number;
  loading: boolean;
}

function Filter({
  closeFilter,
  activeFilterItem,
  setActiveFilterItem,
  filters,
  totalProductCount,
  loading,
}: IFilterProps) {
  const { isPC } = useCheckPC();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const params = searchParams.get("filter");
  const query = qs.parse(location.search.replace("?", ""));
  const sortIndex = Number(searchParams.get("sortIndex")) || 0;

  const [filterQuerystring, setFilterQuerystring] = useState<Record<string, string[]>>({});

  const [mainFilters, setMainFilters] = useState<Record<string, { label: string; count: number }[]>>({});
  const [subFilters, setSubFilters] = useState<Record<string, { label: string; count: number }[]>>({});

  useScrollLock(!isPC);

  useEffect(() => {
    if (params) {
      // JSON.parse 의 반환 타입은 기본적으로 any 이므로 타입 단언을 해주었음.
      setFilterQuerystring(JSON.parse(decodeURIComponent(params)) as Record<string, string[]>);
    } else {
      setFilterQuerystring({});
    }
  }, [params]);

  useEffect(() => {
    setMainFilters(getProductFilters(filters));
    setSubFilters(getSubFilters(filters));
  }, [filters]);

  const getAvailableFiltersById = (
    _filters: Filter[],
    id: string,
  ): {
    label: string;
    count: number;
  }[] => {
    return (
      _filters
        .find((filter) => filter.id === id)
        ?.values.filter((value) => value.count > 0)
        .map((value) => ({ label: value.label, count: value.count })) ?? []
    );
  };

  const getProductFilters = (
    _filters: Filter[],
  ): {
    brand: { label: string; count: number }[];
    category: { label: string; count: number }[];
    color: { label: string; count: number }[];
    size: { label: string; count: number }[];
    sale: { label: string; count: number }[];
  } => {
    return {
      brand: getAvailableFiltersById(_filters, "filter.p.vendor"),
      category: getAvailableFiltersById(_filters, "filter.p.product_type"),
      color: getAvailableFiltersById(_filters, "filter.p.m.filter.color"),
      size: getAvailableFiltersById(_filters, "filter.v.option.size"),
      sale: getAvailableFiltersById(_filters, "filter.p.m.operation.sale"),
    };
  };

  const getSubFilters = (
    _filters: Filter[],
  ): {
    Accessories: { label: string; count: number }[];
    Footwear: { label: string; count: number }[];
    Clothing: { label: string; count: number }[];
  } => {
    const subCategoryFilters = getAvailableFiltersById(_filters, "filter.p.m.category.sub");
    const accessoriesFilter = ["Hats", "Bags", "Eyewear", "Socks & Underwear", "Gloves & Scarves", "Lifestyle"];
    const footwearFilter = ["Sneakers", "Sandals", "Shoes", "Boots"];
    const clothingFilter = ["Outerwear", "T-shirts", "Sweatshirts", "Shirts", "Pants & Skirts"];
    const subCategoryMap = new Map(subCategoryFilters.map((filter) => [filter.label, filter]));

    return {
      Accessories: accessoriesFilter.map((label) => subCategoryMap.get(label)).filter(Boolean) as {
        label: string;
        count: number;
      }[],
      Footwear: footwearFilter.map((label) => subCategoryMap.get(label)).filter(Boolean) as {
        label: string;
        count: number;
      }[],
      Clothing: clothingFilter.map((label) => subCategoryMap.get(label)).filter(Boolean) as {
        label: string;
        count: number;
      }[],
    };
  };

  const onChange = (key: string, value: string) => {
    window.scrollTo({ top: 0 });

    let activeKey = key;
    if (key === "sale") {
      activeKey = activeFilterItem;
    } else {
      const isCategory = [ACCESSORIES, CLOTHING, FOOTWEAR].includes(key);
      if (isCategory) activeKey = CATEGORY;
    }

    setActiveFilterItem(activeKey);

    const values = filterQuerystring[key] ?? []; // ex [Nike, Converse, ... ]
    const isExistedValue = values.includes(value);
    const newValues = isExistedValue ? values.filter((val) => val !== value) : [...values, value];
    const nextFilter = { ...filterQuerystring, [key]: newValues };

    setFilterQuerystring(nextFilter);
    setSearchParams({ ...query, filter: JSON.stringify(nextFilter) });
  };

  const isChecked = (name: string, value: string) => {
    const find = filterQuerystring[name];
    return Boolean(find) && Boolean(find.includes(value));
  };

  const handleReset = () => {
    setFilterQuerystring({});
    setSearchParams({ sortIndex: sortIndex.toString() });
  };

  const hasSaleFilter =
    Object.entries(mainFilters)
      .filter(([filter, _]) => filter === "sale")
      .flatMap(([_, values]) => {
        return values.filter(({ label, count }) => label === "Yes" && count >= 0);
      }).length > 0;

  const isSalePage = location.pathname.includes("sale") || location.pathname.includes("season-off");

  const filterValuesLength = Object.entries(filterQuerystring)
    .map(([key, value]) => value.length)
    .reduce((acc, b) => acc + b, 0);

  return (
    <section
      className="scrollbar-hide pc:sticky pc:top-22.5 pc:z-0 pc:col-start-1 pc:col-end-4 pc:h-fit pc:w-full pc:p-0 fixed inset-0 z-30 flex flex-col gap-5 overflow-y-auto bg-white px-4 pb-20"
      id="filter"
    >
      <Logo className="pc:hidden sticky top-0 flex w-full justify-center bg-white py-2.5" />
      <div className="flex flex-col gap-5">
        <div className="flex justify-between">
          <span>FILTER ({transformComma(totalProductCount)} Products)</span>
          {Object.values(filterQuerystring).some((value) => value.length > 0) && (
            <Button className="underline" onClick={handleReset} type="button" variant="text">
              RESET
            </Button>
          )}
        </div>
        {Object.values(filterQuerystring).length && filterValuesLength ? (
          <ul className="flex flex-wrap gap-1">
            {Object.entries(filterQuerystring).map(([key, value]) =>
              value.map((item) => (
                <li key={item}>
                  <Button
                    id={`btn-${key}`}
                    onClick={() => {
                      onChange(key, item);
                    }}
                    styleType="secondary"
                  >
                    {item}
                    <Icon name={IconName.Close_100} />
                  </Button>
                </li>
              )),
            )}
          </ul>
        ) : null}
      </div>
      <ul className="flex flex-col gap-2.5">
        {/* 카테고리 */}
        {/* SALE 필터 */}
        {/*{!isSalePage &&*/}
        {/*  (hasSaleFilter*/}
        {/*    ? Object.entries(mainFilters)*/}
        {/*        .filter(([filter, _]) => filter === "sale")*/}
        {/*        .map(([filter, value]) => (*/}
        {/*          <div className="pc:gap-2.5 flex flex-col gap-5 overflow-x-visible" key={filter}>*/}
        {/*            {value*/}
        {/*              .filter(({ count, label }) => label === "Yes" && count > 0)*/}
        {/*              .map(({ label }) => (*/}
        {/*                <Checkbox*/}
        {/*                  checked={isChecked(filter, `${filter.toUpperCase()} ONLY`)}*/}
        {/*                  id={filter}*/}
        {/*                  key={label}*/}
        {/*                  label={`${filter.toUpperCase()} ONLY`}*/}
        {/*                  name={filter}*/}
        {/*                  onChange={() => {*/}
        {/*                    onChange(filter, `${filter.toUpperCase()} ONLY`);*/}
        {/*                  }}*/}
        {/*                  value={filter}*/}
        {/*                />*/}
        {/*              ))}*/}
        {/*          </div>*/}
        {/*        ))*/}
        {/*    : null)}*/}
        {/* 그외 필터들 */}
        {Object.entries(mainFilters)
          .filter(([filter, _]) => filter !== "sale")
          .map(([filter, value]) => (
            <FilterList
              handleShowList={() => {
                setActiveFilterItem(filter);
              }}
              key={filter}
              showList={activeFilterItem === filter}
              title={filter}
            >
              {value
                .filter(({ count }) => count > 0)
                .filter(
                  ({ label }) =>
                    !Object.keys(subFilters).includes(label) ||
                    subFilters[label].reduce((sum: number, subFilterValue) => {
                      // eslint-disable-next-line no-param-reassign
                      sum += subFilterValue.count;
                      return sum;
                    }, 0) > 0,
                )
                .map(({ label }) =>
                  filter === "category" ? (
                    <li className="w-fit" key={label}>
                      <span>{label}</span>
                      <div className="pc:gap-2.5 pc:pt-2.5 flex flex-col gap-5 overflow-x-visible pt-5 pl-2.5">
                        {subFilters[label].map((item) => (
                          <Checkbox
                            checked={isChecked(label, String(item.label))}
                            id={item.label}
                            key={item.label}
                            label={item.label}
                            name={item.label}
                            onChange={() => {
                              onChange(label, String(item.label));
                            }}
                            value={item.label}
                          />
                        ))}
                      </div>
                    </li>
                  ) : (
                    <FilterItem
                      checked={isChecked(filter, String(label))}
                      id={label}
                      key={label}
                      label={label}
                      name={filter}
                      onChange={() => {
                        onChange(filter, String(label));
                      }}
                      value={label}
                    />
                  ),
                )}
            </FilterList>
          ))}
      </ul>
      {!isPC && (
        <Button
          className={classNames("fixed! bottom-5 left-1/2 h-9 -translate-x-1/2 px-3 py-2", loading && "w-9.5")}
          isLoading={loading}
          onClick={closeFilter}
          styleType="secondary"
          variant={
            !loading && Object.values(filterQuerystring).some((value) => value.length > 0)
              ? "contained"
              : "containedIcon"
          }
        >
          {Object.values(filterQuerystring).some((value) => value.length > 0) ? (
            <span className={classNames(loading && "invisible relative")}>Apply Filter</span>
          ) : (
            <Icon name={IconName.Close_100} />
          )}
        </Button>
      )}
    </section>
  );
}

export default Filter;
